/**
 * سفرِ کاربر — «از کجا آمد، کجاها رفت، چقدر ماند، چه زد، از کجا رفت».
 *
 * ═══ چه چیزی این‌جا هست و چه چیزی عمداً نیست ═══
 *
 * چهار سوال جواب می‌گیرند و هر کدام یک بخش‌اند: جریانِ کلی (دو نمودار در
 * دو تب)، صفحه‌ها (چقدر ماندند، کجا رها کردند)، دکمه‌ها (روی هر صفحه چه
 * زدند)، و تک‌تکِ نشست‌ها (سفرِ واقعیِ یک نفر، به ترتیب).
 *
 * ═══ چرا «جریان» دو نمودار است و نه یکی (WHOSUP-241) ═══
 *
 * سنکی ستون‌هایش **پله**اند — اولین صفحه، دومین، سومین — و همین بود که
 * اجازه می‌داد حلقه‌ی «کشف → رویداد → کشف» اصلاً کشیده شود: سنکی حلقه
 * نمی‌کشد. ولی بهایش این است که یک صفحه در آن چند گره دارد، و هیچ‌جا
 * نمی‌شود پرسید «روی هم چند نفر وارد کشف شدند».
 *
 * تبِ دوم دقیقاً همان سوال را جواب می‌دهد: هر آدرس **یک** گره، یال‌های
 * جهت‌دار، و حلقه آزاد. جانشینِ سنکی نیست و قرار هم نیست بشود — دو سوالِ
 * متفاوت، دو شکل. توضیحِ خودِ گراف سرِ `JourneyGraph.tsx`.
 *
 * ═══ ⚠ ستونِ دو-عددیِ بقیه‌ی داشبورد این‌جا نیست ═══
 *
 * هر عددِ این صفحه یکی است، چون پنجره‌اش یکی است: «تازه‌ترین N نشست». دو
 * ستون گذاشتن یعنی ادعای مقایسه‌ای که داده‌اش را نداریم.
 *
 * ⚠ حساب‌های داخلی (خودمان و تستی‌ها) پیش از رسیدن به این‌جا کنار رفته‌اند —
 * در خودِ `admin_journeys`، با همان قاعده‌ی مایگریشنِ ۰۱۱۹.
 *
 * ═══ فیلترِ سطح (WHOSUP-242) ═══
 *
 * سفر تنها بخشِ این سربرگ است که **نمی‌شود** دو سطح را رویش هم کشید: یک
 * نشست از اول تا آخر یا وب است یا موبایل، و «میانه‌ی مدتِ نشست»ِ قاطی
 * میانه‌ی هیچ‌کس نیست. پس این‌جا جدا کردن تنها راهِ درست است و نه یک
 * سلیقه — و `surface`ِ بالای سربرگ همان را می‌کند.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import {
  buildJourneyReport, EXIT, OTHER,
  type JourneyData, type JourneyStep, type JourneyTap,
} from '@/core/lib/journeyReport';
import type { SurfaceFilter } from '@/core/lib/activityReport';
import { SankeyChart } from '@/features/crawler/SankeyChart';
import { JourneyGraph } from './JourneyGraph';
import { fmtNum as fmt } from '@/ui/Num';
import { SURFACE_FA } from './surface';

/* ─────────────────────────── نمایشِ مقدارها ─────────────────────────── */

/** مدت، به بزرگ‌ترین واحدی که هنوز دقیق است. `null` یعنی اندازه نگرفتیم. */
export function dur(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} ثانیه`;
  const m = s / 60;
  if (m < 60) return `${m < 10 ? m.toFixed(1) : Math.round(m)} دقیقه`;
  return `${(m / 60).toFixed(1)} ساعت`;
}

const pct = (x: number): string => `${Math.round(x * 100)}٪`;

/** نام‌های ثابتِ گراف و ورودی. متن این‌جاست چون فقط این صفحه نشانشان می‌دهد. */
const ENTRY_FA: Record<string, string> = {
  direct: 'مستقیم',
  referral: 'از سایتِ دیگر',
  invite: 'لینکِ دعوت',
  share: 'لینکِ عمومی',
  deeplink: 'لینکِ اپ',
  unknown: 'نامعلوم',
};

const screenLabel = (s: string): string =>
  (s === EXIT ? 'خروج' : s === OTHER ? 'بقیه' : s);

/**
 * دو نمای «جریانِ سفر» (WHOSUP-241).
 *
 * ⚠ جانشینِ هم نیستند و هیچ‌کدام قرار نیست آن یکی را بردارد: سنکی جوابِ
 * «کاربر از پله‌ی اول به کجا می‌رود» است و هر صفحه در آن چند گره دارد؛
 * گراف جوابِ «روی هم چند نفر وارد این صفحه شدند و چند نفرشان همان‌جا
 * رها کردند» است و هر صفحه در آن یک گره. دو سوالِ متفاوت با دو شکل، پس
 * دو تب — و نه یک نمودارِ دورگه که هیچ‌کدام را درست جواب ندهد.
 */
type View = 'steps' | 'graph';

const VIEWS: { id: View; label: string }[] = [
  { id: 'steps', label: 'پله‌به‌پله (سنکی)' },
  { id: 'graph', label: 'گرافِ صفحه‌ها' },
];

/* ─────────────────────────── خودِ سربرگ ─────────────────────────── */

export function Journey({ surface = 'all' }: { surface?: SurfaceFilter }) {
  const q = useQuery({ queryKey: ['journeys'], queryFn: () => opsRepo.journeys() });
  const [openSession, setOpenSession] = React.useState<string | null>(null);
  const [view, setView] = React.useState<View>('steps');
  /**
   * ⚠ سقفِ نمایش، نه سقفِ داده: هر ۳۰۰ نشست در نمودارها و جدول‌های بالا
   * هستند و فقط این فهرست کوتاه می‌شود. سه‌صد ردیفِ باز، صفحه را چهارده
   * هزار پیکسل بلند می‌کرد و بقیه‌ی کارت‌ها را عملاً ناپیدا.
   */
  const [shown, setShown] = React.useState(50);

  const data = q.data as JourneyData | undefined;
  /**
   * ⚠ فیلترِ سطح این‌جا اعمال می‌شود و نه در کوئری: داده همان «تازه‌ترین
   * ۳۰۰ نشست»ِ هر دو سطح است و هر نشستش `surface` دارد، پس تعویضِ سطح یک
   * محاسبه‌ی دوباره است و نه یک رفت‌وبرگشتِ شبکه. معنیِ پنجره هم عوض
   * نمی‌شود و جمله‌ی زیرِ کارتِ نشست همان را می‌گوید.
   */
  const report = React.useMemo(
    () => (data ? buildJourneyReport(data, { surface }) : null),
    [data, surface],
  );
  const sessions = React.useMemo(
    () => (data ? data.sessions.filter((s) => surface === 'all' || s.surface === surface) : []),
    [data, surface],
  );

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) {
    return (
      <div className="card">
        <p className="err">{String(q.error)}</p>
        {/* روی دیتابیسی که هنوز ۰۱۲۰ را ندارد، «تابع پیدا نشد» می‌آید — و
            این دقیقاً همان چیزی است که باید نوشته شود، نه یک صفحه‌ی خالی. */}
        <p className="note">
          اگر پیام «تابع پیدا نشد» است، مایگریشنِ ۰۱۲۰ روی این دیتابیس اجرا نشده.
        </p>
      </div>
    );
  }
  if (!report || !data) return null;

  const { totals, screens, buttons, entries, flow } = report;

  if (!totals.sessions) {
    return (
      <p className="note">
        {surface === 'all' ? (
          <>
            هنوز هیچ نشستی ثبت نشده. ثبتِ سفر از مایگریشنِ ۰۱۲۰ شروع می‌شود و
            نسخه‌ی اپ/سایتِ بعد از آن لازم است؛ نشست‌های قدیمی‌تر پله و مدت ندارند.
          </>
        ) : (
          <>
            در این پنجره هیچ نشستی از <b>{SURFACE_FA[surface]}</b> نیست.
            {' '}{data.sessions.length ? `(${fmt(data.sessions.length)} نشستِ سطحِ دیگر هست.)` : ''}
          </>
        )}
      </p>
    );
  }

  return (
    <>
      <div className="grid n4" style={{ marginTop: 12 }}>
        <One label={surface === 'all' ? 'نشست' : `نشستِ ${SURFACE_FA[surface]}`}
          v={fmt(totals.sessions)} sub={windowNote(data, surface, totals.sessions)} />
        <One label="میانه‌ی مدتِ نشست" v={dur(totals.medianSessionMs)} />
        <One label="میانه‌ی صفحه در نشست" v={fmt(totals.medianStepsPerSession)} />
        <One label="تک‌صفحه‌ای" v={pct(totals.bounceRate)}
          sub={`${fmt(totals.bounced)} نشست فقط یک صفحه دید`} />
      </div>

      {/* ─────────── ۱ ــ جریان ─────────── */}
      <div className="card">
        <h2>جریانِ سفر</h2>
        <div className="chiprow" style={{ marginBottom: 8 }}>
          {VIEWS.map((v) => (
            <button key={v.id} type="button"
              className={`chip${view === v.id ? ' on' : ''}`}
              onClick={() => setView(v.id)}>{v.label}</button>
          ))}
        </div>

        {view === 'steps' ? (
          <>
            <p className="note">
              ستونِ اول <b>اولین</b> صفحه‌ی هر نشست است، ستونِ دوم دومین، و همین‌طور
              تا پنجمین. ضخامتِ هر جریان یعنی چند نشست آن مسیر را رفتند. گرهِ
              «خروج» یعنی نشست همان‌جا تمام شد، و «بقیه» یعنی چند صفحه‌ی کم‌تکرارِ
              همان ستون — هیچ نشستی حذف نمی‌شود، فقط با هم جمع می‌شوند.
            </p>
            {flow.nodes.length ? (
              <SankeyChart
                nodes={flow.nodes.map((n) => ({ ...n, label: screenLabel(n.label) }))}
                links={flow.links} height={460} nodePadding={26} />
            ) : <p className="dim">—</p>}
          </>
        ) : (
          <>
            <p className="note">
              هر آدرسِ صفحه یک گره است — نه یکی به ازای هر پله — پس حلقه هم
              دیده می‌شود (کشف → رویداد → کشف)، که سنکی ذاتاً نمی‌کشدش.
              اندازه‌ی هر گره تعدادِ <b>ورود</b> به آن صفحه است و پیکانِ وسطِ
              هر یال جهتش را می‌گوید. گرهِ «خروج» این‌جا نیست و جایش
              <b> رنگ</b> است: سبز یعنی هیچ‌کس از آن صفحه بیرون نرفت، قرمز
              یعنی صد درصد، و وسطِ طیف یعنی نصف‌نصف.
            </p>
            <p className="note">
              گره‌ها را می‌شود <b>با موس جابه‌جا کرد</b> — بگیر و بکش. چیدمانی
              که می‌سازی در همین مرورگر می‌ماند، و دکمه‌ی «چیدمانِ خودکار»
              برش می‌گرداند.
            </p>
            <JourneyGraph data={data} surface={surface} />
          </>
        )}
      </div>

      {/* ─────────── ۲ ــ ورودی ─────────── */}
      <div className="card">
        <h2>از کجا آمدند</h2>
        <Bars rows={entries.map((e) => ({
          key: `${e.entry}:${e.ref ?? ''}`,
          label: ENTRY_FA[e.entry] ?? e.entry,
          note: e.ref ?? undefined,
          value: e.sessions,
        }))} unit="نشست" />
        <p className="note">
          از ارجاع‌دهنده فقط <b>میزبانش</b> نگه داشته می‌شود، نه مسیرش: مسیرِ
          صفحه‌ی ارجاع‌دهنده می‌تواند خودش داده‌ی کسی باشد.
          {' '}«نامعلوم» یعنی نشستی که پیش از مایگریشنِ ۰۱۲۰ شروع شده و
          رویدادِ ورود ندارد.
        </p>
      </div>

      {/* ─────────── ۳ ــ صفحه‌ها ─────────── */}
      <div className="card">
        <h2>هر صفحه، چقدر</h2>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>صفحه</th>
                <th className="num">بازدید</th>
                <th className="num">نشست</th>
                <th className="num">میانه</th>
                <th className="num">۹۰٪</th>
                <th className="num">ورود</th>
                <th className="num">خروج</th>
                <th className="num">نرخِ خروج</th>
              </tr>
            </thead>
            <tbody>
              {screens.map((s) => (
                <tr key={s.screen}>
                  <td className="mono ltr">{s.screen}</td>
                  <td className="num">{fmt(s.views)}</td>
                  <td className="num">{fmt(s.sessions)}</td>
                  <td className="num">{dur(s.medianMs)}</td>
                  <td className="num">{dur(s.p90Ms)}</td>
                  <td className="num">{fmt(s.entries)}</td>
                  <td className="num">{fmt(s.exits)}</td>
                  {/* بالای هفتاد درصد یعنی این صفحه ته‌خطِ اغلبِ سفرهاست —
                      که برای صفحه‌ی «انجام شد» طبیعی است و برای بقیه نه. */}
                  <td className={`num${s.exitRate >= 0.7 ? ' bad' : ''}`}>{pct(s.exitRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          «میانه» فقط از مدت‌های <b>معلوم</b> حساب می‌شود. صفحه‌ای که کاربر
          رویش بوده و بعد اپ را کشته، مدتِ آخرش نامعلوم می‌ماند و صفر شمرده
          نمی‌شود — وگرنه دقیقاً برعکسِ واقعیت خوانده می‌شد.
        </p>
      </div>

      {/* ─────────── ۴ ــ دکمه‌ها ─────────── */}
      <div className="card">
        <h2>روی هر صفحه چه زدند</h2>
        {buttons.length ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>صفحه</th><th>دکمه</th>
                  <th className="num">لمس</th><th className="num">نشست</th>
                </tr>
              </thead>
              <tbody>
                {buttons.slice(0, 60).map((b) => (
                  <tr key={`${b.screen} ${b.label}`}>
                    <td className="mono ltr">{b.screen}</td>
                    <td className="mono ltr">{b.label}</td>
                    <td className="num">{fmt(b.taps)}</td>
                    <td className="num">{fmt(b.sessions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="dim">—</p>}
        <p className="note">
          نامِ دکمه <b>کلیدِ ترجمه</b> است و نه متنی که کاربر دید: متنِ روی
          دکمه گاهی اسمِ یک دوست یا تگ است و آن در جدولِ رفتار نمی‌نشیند.
          {' '}<span className="mono">other</span> یعنی دکمه‌ای که متنش کلیدِ
          ثابتی نداشت — تعدادش خودش خبر است، پس بی‌صدا انداخته نمی‌شود.
        </p>
      </div>

      {/* ─────────── ۵ ــ تک‌تکِ سفرها ─────────── */}
      <div className="card">
        <h2>سفرها</h2>
        <p className="note">
          روی هر ردیف بزن تا مسیرش باز شود: هر صفحه به ترتیب، با مدتی که
          رویش ماند و دکمه‌هایی که همان‌جا زد.
        </p>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>کِی</th><th>ورود</th><th>سطح</th>
                <th className="num">صفحه</th><th className="num">لمس</th>
                <th className="num">مدت</th><th>پایان</th>
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, shown).map((s) => (
                <React.Fragment key={s.session_id}>
                  <tr onClick={() => setOpenSession(
                    openSession === s.session_id ? null : s.session_id)}
                    style={{ cursor: 'pointer' }}>
                    <td className="dim ltr">{new Date(s.started).toLocaleString('en-CA')}</td>
                    <td>
                      {ENTRY_FA[s.entry] ?? s.entry}
                      {s.ref ? <span className="dim mono ltr"> {s.ref}</span> : null}
                    </td>
                    <td className="dim">
                      <span className={`swatch ${s.surface}`} />
                      {SURFACE_FA[s.surface]}
                      {s.signed_in ? '' : ' · مهمان'}
                    </td>
                    <td className="num">{fmt(s.views)}</td>
                    <td className="num">{fmt(s.taps)}</td>
                    <td className="num">{dur(s.ms)}</td>
                    <td className="mono ltr">{s.exit_screen ?? '—'}</td>
                  </tr>
                  {openSession === s.session_id ? (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bg2)' }}>
                        <Timeline
                          steps={data.steps.filter((x) => x.session_id === s.session_id)}
                          taps={data.taps.filter((x) => x.session_id === s.session_id)} />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {/* سقف هیچ‌وقت بی‌صدا نیست: عددِ نمایش‌داده‌شده و عددِ کل کنارِ هم. */}
        <div className="chiprow" style={{ marginTop: 8 }}>
          <span className="dim">
            {fmt(Math.min(shown, sessions.length))} از {fmt(sessions.length)} نشست
            {surface === 'all' ? '' : ` — فقط ${SURFACE_FA[surface]}`}
          </span>
          {shown < sessions.length ? (
            <button type="button" className="chip" onClick={() => setShown((n) => n + 50)}>
              ۵۰ تای بعدی
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── تکه‌های کوچک ─────────────────────────── */

/**
 * ⚠ زیرِ فیلترِ سطح، پنجره **عوض نمی‌شود**: داده همان تازه‌ترین N نشستِ هر
 * دو سطح است و این‌جا فقط بخشی‌اش می‌ماند. پس «۴۰ نشستِ موبایل» یعنی چهل
 * تا از همان N، نه تازه‌ترین چهل نشستِ موبایل — و اگر نوشته نشود، سطحِ
 * کم‌ترافیک بی‌صدا کوچک‌تر از آنچه هست به نظر می‌رسد.
 */
const windowNote = (d: JourneyData, surface: SurfaceFilter, shown: number): string => {
  const from = d.window?.from ? new Date(d.window.from).toLocaleDateString('en-CA') : null;
  const to = d.window?.to ? new Date(d.window.to).toLocaleDateString('en-CA') : null;
  const when = from && to ? `${from} تا ${to}` : 'تازه‌ترین نشست‌ها';
  return surface === 'all' ? when
    : `${shown} از ${d.sessions.length} نشستِ همین پنجره · ${when}`;
};

/** کارتِ تک‌عددی — قرینه‌ی `Stat` ولی بی ستونِ دوم، چون پنجره یکی است. */
function One({ label, v, sub }: { label: string; v: string; sub?: string }) {
  return (
    <div className="card stat">
      <div className="n">{v}</div>
      <div className="k">{label}</div>
      {sub ? <div className="k" style={{ fontSize: 11, color: 'var(--ink3)' }}>{sub}</div> : null}
    </div>
  );
}

/**
 * میله‌های افقی — نسبت به بزرگ‌ترین مقدار.
 *
 * عمداً بدونِ کتابخانه: یک `div` با عرضِ درصدی همان کاری را می‌کند که یک
 * نمودارِ میله‌ای، و وابستگیِ تازه نمی‌خواهد.
 */
function Bars({ rows, unit }: {
  rows: { key: string; label: string; note?: string; value: number }[];
  unit: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="dim">—</p>;
  return (
    <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1fr 3fr auto', gap: 8, alignItems: 'center' }}>
          <span>
            {r.label}
            {r.note ? <span className="dim mono ltr"> {r.note}</span> : null}
          </span>
          <span style={{ background: 'var(--bg2)', borderRadius: 4, overflow: 'hidden' }}>
            <span style={{
              display: 'block', height: 10, width: `${(r.value / max) * 100}%`,
              background: 'var(--brand)',
            }} />
          </span>
          <span className="num dim">{fmt(r.value)} {unit}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * یک سفرِ واقعی، به ترتیب.
 *
 * ⚠ لمس‌ها زیرِ **پله‌ی خودشان** می‌آیند و نه در یک فهرستِ جدا: «چه دکمه‌ای
 * روی کدام صفحه» تمامِ سوال است، و جدا کردنشان همان چیزی است که این صفحه
 * برای درست کردنش ساخته شد.
 */
function Timeline({ steps, taps }: { steps: JourneyStep[]; taps: JourneyTap[] }) {
  if (!steps.length) return <p className="dim">پله‌ای ثبت نشده.</p>;
  return (
    <ol style={{ margin: 0, padding: '4px 18px 4px 0' }}>
      {steps.map((s) => {
        const mine = taps.filter((t) => t.step === s.step);
        return (
          <li key={s.step} style={{ padding: '3px 0' }}>
            <span className="mono ltr">{s.screen}</span>
            <span className="dim"> · {dur(s.ms)}</span>
            {s.via ? <span className="dim"> · با <span className="mono ltr">{s.via}</span></span> : null}
            {mine.length ? (
              <div className="chiprow" style={{ marginTop: 2 }}>
                {mine.map((t, i) => (
                  <span key={i} className="chip mono ltr">{t.label}</span>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
